import { Component } from '@theme/component';

/**
 * WDW Case-Slider (Startseite): ARIA-Tablist mit Favicon-Tabs, kein Autoplay.
 * Klick oder Pfeiltasten (links/rechts, Pos1/Ende) wechseln den aktiven Case.
 * Die Wechsel-Animation (Crossfade, gleitender Indikator, Count-up) folgt in Phase 9 über data-state.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} tablist - Container der Tabs
 * @property {HTMLButtonElement[]} tabs - Tabs
 *
 * @extends {Component<Refs>}
 */
class WdwCaseSliderComponent extends Component {
  requiredRefs = ['tablist'];

  /** @returns {HTMLButtonElement[]} */
  get tabs() {
    return /** @type {HTMLButtonElement[]} */ ([...this.querySelectorAll('[data-case-tab]')]);
  }

  /**
   * Tab per Klick aktivieren (on:click="/select").
   * @param {Event} event
   */
  select(event) {
    const tab = /** @type {HTMLElement} */ (event.target).closest('[data-case-tab]');
    if (tab instanceof HTMLButtonElement) this.#activate(tab, false);
  }

  /**
   * Tastatursteuerung der Tablist (on:keydown="/onKeydown").
   * @param {KeyboardEvent} event
   */
  onKeydown(event) {
    const tabs = this.tabs;
    const current = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    let next = -1;

    if (event.key === 'ArrowRight') next = (current + 1) % tabs.length;
    else if (event.key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;

    if (next < 0) return;
    event.preventDefault();
    const nextTab = tabs[next];
    if (nextTab) this.#activate(nextTab, true);
  }

  /**
   * @param {HTMLButtonElement} tab
   * @param {boolean} focus
   */
  #activate(tab, focus) {
    for (const other of this.tabs) {
      const selected = other === tab;
      other.setAttribute('aria-selected', String(selected));
      other.tabIndex = selected ? 0 : -1;
      const panel = document.getElementById(other.getAttribute('aria-controls') ?? '');
      if (panel) panel.hidden = !selected;
    }
    if (focus) tab.focus();
    this.dispatchEvent(new CustomEvent('wdw:case-change', { detail: { tab }, bubbles: true }));
  }
}

if (!customElements.get('wdw-case-slider-component')) {
  customElements.define('wdw-case-slider-component', WdwCaseSliderComponent);
}
